import React, {useState} from 'react';
import {Button, Form, FormGroup, Input, Label, Modal, Spinner} from 'reactstrap';
import PropTypes from 'prop-types';
import {Ai} from "../../services/api/Ai";
import {useApiErrorDispatch} from "../../context/api_error/ApiErrorIndex";
import {useProjectState} from "../../context/project/ProjectIndex";
import {v4 as uuid4} from "uuid";
import {Assets} from "../../services/api/Assets";
import {addCanvasObject, useCanvasState} from "../../context/canvas/CanvasIndex";

const AIImageModal = ({isOpen, onClose, companyId, projectId, canvasDispatch}) => {
  const apiErrorDispatch = useApiErrorDispatch()
  const canvasState = useCanvasState()
  const projectState = useProjectState()
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('256x256');
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);

  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
  };

  const handleSizeChange = (e) => {
    setSize(e.target.value);
  };

  const handleGenerateImage = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setLoading(true);
    setError(null);


    try {
      Ai.post(projectState.companyId, projectState.projectId, {'prompt': prompt, 'size': size})
        .then((r) => {
          if (r?.status < 400) {
            setGeneratedImage(r.data);
            setLoading(false);
          } else {
            setError('Failed to generate image. Please try again.');
            setLoading(false);
          }
        })
    } catch (err) {
    }
  };

  const handleAddToCanvas = async () => {
    try {
      let id = uuid4();
      let payload = {
        path: generatedImage.url,
        name: `AI Image: ${prompt.substring(0, 30)}${prompt.length > 30 ? '...' : ''}`,
        type: 'image',
      };
      payload.status = 1
      setLoading(true)
      Assets.put(projectState.companyId, projectState.projectId, id, payload).then((r) => {
        if (r?.status < 400) {
          addCanvasObject(canvasDispatch, 'image', id)
          setLoading(false)
          onClose();
        } else {
          setLoading(false);
        }
      });


    } catch (err) {
      setError('Failed to add image to canvas. Please try again.');
      console.error('Error adding AI image to canvas:', err);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} className="modal-dialog-centered">
      <div className="modal-header">
        <h5 className="modal-title">Generate AI Image</h5>
        <button
          aria-label="Close"
          className="btn-close"
          data-dismiss="modal"
          type="button"
          onClick={onClose}
        />
      </div>
      <div className="modal-body">
        {!generatedImage ? (
          <Form>
            <FormGroup>
              <Label for="imagePrompt">Describe the image you want to generate</Label>
              <Input
                style={{minHeight: 200}}
                type="textarea"
                id="imagePrompt"
                placeholder="E.g., A cute cat wearing sunglasses on a beach"
                value={prompt}
                onChange={handlePromptChange}
                rows={4}
              />
            </FormGroup>
            <FormGroup>
              <Label for="imageSize">Select image size</Label>
              <Input
                type="select"
                id="imageSize"
                value={size}
                onChange={handleSizeChange}
              >
                <option value="256x256">Small (256x256)</option>
                <option value="512x512">Medium (512x512)</option>
                <option value="1024x1024">Large (1024x1024)</option>
              </Input>
            </FormGroup>
            {error && <div className="text-danger mb-3">{error}</div>}
          </Form>
        ) : (
          <div className="text-center">
            <img
              src={generatedImage.url}
              alt="Generated AI Image"
              className="img-fluid mb-3 border rounded"
              style={{maxHeight: '300px'}}
            />
            <p className="text-muted small">Prompt: {prompt}</p>
          </div>
        )}
      </div>
      <div className="modal-footer">
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        {!generatedImage ? (
          <Button color="primary" onClick={handleGenerateImage} disabled={loading || !prompt.trim()}>
            {loading ? <><Spinner size="sm"/> Generating...</> : 'Generate Image'}
          </Button>
        ) : (
          <>
            <Button color="secondary" onClick={() => setGeneratedImage(null)}>
              Try Again
            </Button>
            <Button color="primary" onClick={handleAddToCanvas}>
              {loading ? <><Spinner size="sm"/> Adding...</> : 'Add to Canvas'}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

AIImageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  companyId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  canvasDispatch: PropTypes.func.isRequired
};

export default AIImageModal;
